import { useState, useMemo } from 'react';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { Modal } from '@/components/molecules';
import {
  useProductsList,
  useProductVariantsList,
  useMultipleVariantPrices,
} from '@/hooks/useErpApi';
import { BulkPriceUpdate } from '@/components/organisms/ProductPrice/BulkPriceUpdate';
import { ProductVariantSelector } from '@/components/organisms/ProductPrice/ProductVariantSelector';
import { MultiVariantPriceForm } from '@/components/organisms/ProductPrice/MultiVariantPriceForm';

export default function ProductPricesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriceType, setSelectedPriceType] = useState<string>('all');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [isPriceFormOpen, setIsPriceFormOpen] = useState(false);
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [priceFormStep, setPriceFormStep] = useState<'select' | 'create'>('select');
  const [selectedVariants, setSelectedVariants] = useState<
    Array<{ id: string; name: string; productName: string }>
  >([]);

  const {
    data: products,
    isLoading: productsLoading,
    error: productsError,
    refetch,
  } = useProductsList();

  const priceTypes = ['all', 'msp', 'member', 'retail', 'mrp'];

  const toggleProduct = (productId: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const filteredProducts = useMemo(() => {
    if (!products) return [];

    return products.filter((product) => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [products, searchQuery]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (productsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="IndianRupee" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading pricing data...</p>
        </div>
      </div>
    );
  }

  if (productsError) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <Icon name="CircleAlert" size={24} className="text-error-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-error-900 mb-2">Failed to Load Products</h3>
              <p className="text-error-700 mb-4">{productsError.message}</p>
              <Button variant="primary" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalProducts = products?.length || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Product Pricing</h1>
          <p className="text-neutral-600 mt-2">
            Manage pricing for product variants across SALE, PURCHASE, and MRP types
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setIsBulkUpdateOpen(true)}>
            <Icon name="Upload" size={20} className="mr-2" />
            Bulk Update
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setPriceFormStep('select');
              setSelectedVariants([]);
              setIsPriceFormOpen(true);
            }}
          >
            <Icon name="Plus" size={20} className="mr-2" />
            Add Price
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Total Products</p>
            <div className="bg-primary-50 p-2 rounded-lg">
              <Icon name="Package" size={20} className="text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalProducts}</p>
          <p className="text-xs text-neutral-500 mt-1">Expand products to view variant pricing</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Price Types</p>
            <div className="bg-info-50 p-2 rounded-lg">
              <Icon name="Layers" size={20} className="text-info-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">3</p>
          <p className="text-xs text-neutral-500 mt-1">SALE, PURCHASE, MRP</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Quick Actions</p>
            <div className="bg-success-50 p-2 rounded-lg">
              <Icon name="Zap" size={20} className="text-success-600" />
            </div>
          </div>
          <p className="text-sm text-neutral-700 mt-2">
            Click <Icon name="Eye" size={14} className="inline" /> to view variant prices
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Products
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={18} className="text-neutral-400" />
              </div>
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by product name..."
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by Price Type
            </label>
            <select
              value={selectedPriceType}
              onChange={(e) => setSelectedPriceType(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {priceTypes.map((type) => (
                <option key={type} value={type}>
                  {type === 'all' 
                    ? 'All Price Types' 
                    : type === 'msp' 
                    ? 'msp (maximum selling price)'
                    : type === 'mrp'
                    ? 'mrp (maximum retail price)'
                    : type}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Variants
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredProducts.map((product) => {
                const isExpanded = expandedProducts.has(product.id);
                return (
                  <ProductRow
                    key={product.id}
                    product={product}
                    isExpanded={isExpanded}
                    onToggle={() => toggleProduct(product.id)}
                    selectedPriceType={selectedPriceType}
                    formatCurrency={formatCurrency}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Icon name="IndianRupee" size={48} className="text-neutral-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-600">No products found</p>
            <p className="text-sm text-neutral-500 mt-1">
              {searchQuery ? 'Try adjusting your search' : 'Add products to start managing prices'}
            </p>
          </div>
        )}
      </div>

      {/* Price Form Modal */}
      <Modal
        isOpen={isPriceFormOpen}
        onClose={() => {
          setIsPriceFormOpen(false);
          setPriceFormStep('select');
          setSelectedVariants([]);
        }}
        title={priceFormStep === 'select' ? 'Add Price - Select Variants' : 'Add Price - Set Price'}
        size="lg"
      >
        <div className="p-6">
          {priceFormStep === 'select' ? (
            <ProductVariantSelector
              onSelect={(variants) => {
                setSelectedVariants(variants);
                setPriceFormStep('create');
              }}
              onCancel={() => {
                setIsPriceFormOpen(false);
                setPriceFormStep('select');
                setSelectedVariants([]);
              }}
            />
          ) : (
            <MultiVariantPriceForm
              variants={selectedVariants}
              onSuccess={() => {
                setIsPriceFormOpen(false);
                setPriceFormStep('select');
                setSelectedVariants([]);
                refetch();
              }}
              onCancel={() => {
                setIsPriceFormOpen(false);
                setPriceFormStep('select');
                setSelectedVariants([]);
              }}
              onBack={() => {
                setPriceFormStep('select');
              }}
            />
          )}
        </div>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal
        isOpen={isBulkUpdateOpen}
        onClose={() => setIsBulkUpdateOpen(false)}
        title="Bulk Price Update"
        size="xl"
      >
        <div className="p-6">
          <BulkPriceUpdate
            onSuccess={() => {
              setIsBulkUpdateOpen(false);
              refetch();
            }}
            onCancel={() => setIsBulkUpdateOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
}

// Separate component for product row to handle variant fetching independently
function ProductRow({
  product,
  isExpanded,
  onToggle,
  selectedPriceType,
  formatCurrency,
}: {
  product: { id: string; name: string; description?: string };
  isExpanded: boolean;
  onToggle: () => void;
  selectedPriceType: string;
  formatCurrency: (amount: number) => string;
}) {
  const { data: variants, isLoading: variantsLoading } = useProductVariantsList(
    product.id,
    isExpanded
  );

  // Extract variant IDs for batch price fetching
  const variantIds = variants ? variants.map((v: { id: string }) => v.id) : [];

  // Fetch prices for all variants at once
  const { data: pricesMap, isLoading: pricesLoading } = useMultipleVariantPrices(
    isExpanded ? variantIds : []
  );

  return (
    <>
      <tr className="hover:bg-neutral-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <button
              onClick={onToggle}
              className="mr-3 text-neutral-400 hover:text-neutral-600 transition-transform"
              style={{
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <Icon name="ChevronRight" size={20} />
            </button>
            <div>
              <p className="font-medium text-neutral-900">{product.name}</p>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Badge variant="secondary" size="sm">
            {product.description
              ? product.description.substring(0, 30) +
                (product.description.length > 30 ? '...' : '')
              : 'N/A'}
          </Badge>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-neutral-700">
          {variants ? variants.length : '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <button
            onClick={onToggle}
            className="text-primary-600 hover:text-primary-900 p-2 rounded-lg hover:bg-primary-50"
            title="View Variants & Prices"
          >
            <Icon name="Eye" size={16} />
          </button>
        </td>
      </tr>

      {/* Expanded Variants */}
      {isExpanded && (
        <tr>
          <td colSpan={4} className="px-6 py-4 bg-neutral-50">
            {variantsLoading || pricesLoading ? (
              <div className="text-center py-8">
                <Icon
                  name="LoaderCircle"
                  size={32}
                  className="animate-spin text-neutral-400 mx-auto mb-2"
                />
                <p className="text-sm text-neutral-600">
                  {variantsLoading ? 'Loading variants...' : 'Loading prices...'}
                </p>
              </div>
            ) : variants && variants.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-neutral-700 mb-3">
                  Product Variants ({variants.length})
                </p>
                {variants.map((variant) => (
                  <VariantPriceRow
                    key={variant.id}
                    variant={variant}
                    prices={pricesMap?.[variant.id] || []}
                    selectedPriceType={selectedPriceType}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Package" size={32} className="text-neutral-300 mx-auto mb-2" />
                <p className="text-sm text-neutral-600">No variants found</p>
                <p className="text-xs text-neutral-500 mt-1">
                  Create variants for this product first
                </p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Component for variant price row
function VariantPriceRow({
  variant,
  prices,
  selectedPriceType,
  formatCurrency,
}: {
  variant: { id: string; variant_name?: string; brand_name?: string; sku?: string };
  prices: unknown[];
  selectedPriceType: string;
  formatCurrency: (amount: number) => string;
}) {
  // Normalize price type to handle backend variations
  const normalizePriceType = (type: string): string => {
    const normalized = type.toLowerCase();
    // Handle legacy values
    if (normalized === 'selling_price' || normalized === 'sale') return 'retail';
    if (normalized === 'purchase_price' || normalized === 'purchase') return 'msp';
    if (normalized === 'mrp' || normalized === 'maximum_retail_price') return 'mrp';
    // Return as-is if already in new format
    return normalized;
  };

  // Get display label for price type
  const getPriceTypeLabel = (type: string): string => {
    const normalized = normalizePriceType(type);
    switch (normalized) {
      case 'msp':
        return 'msp (maximum selling price)';
      case 'mrp':
        return 'mrp (maximum retail price)';
      case 'member':
        return 'member';
      case 'retail':
        return 'retail';
      default:
        // Show original type if not recognized
        return type;
    }
  };

  const activePrices = prices
    ? prices.filter((p: unknown) => (p as { is_active: boolean }).is_active)
    : [];

  // Show all prices, not just the 4 standard types
  const filteredPrices = activePrices.filter((p: unknown) => {
    if (selectedPriceType === 'all') return true;
    const priceType = normalizePriceType((p as { price_type: string }).price_type);
    return priceType === selectedPriceType;
  });

  if (filteredPrices.length === 0 && selectedPriceType !== 'all') return null;

  // Get color scheme for price type
  const getPriceTypeColor = (type: string): { bg: string; text: string; label: string } => {
    const normalized = normalizePriceType(type);
    switch (normalized) {
      case 'msp':
        return { bg: 'bg-primary-50', text: 'text-primary-900', label: 'text-primary-700' };
      case 'member':
        return { bg: 'bg-success-50', text: 'text-success-900', label: 'text-success-700' };
      case 'retail':
        return { bg: 'bg-warning-50', text: 'text-warning-900', label: 'text-warning-700' };
      case 'mrp':
        return { bg: 'bg-info-50', text: 'text-info-900', label: 'text-info-700' };
      default:
        return { bg: 'bg-neutral-50', text: 'text-neutral-900', label: 'text-neutral-700' };
    }
  };

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <p className="font-medium text-neutral-900">
              {variant.variant_name || variant.brand_name || 'Variant'}
            </p>
            {variant.sku && (
              <Badge variant="secondary" size="sm">
                SKU: {variant.sku}
              </Badge>
            )}
          </div>

          {filteredPrices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              {filteredPrices.map((price: unknown, index: number) => {
                const priceObj = price as { price_type: string; price: number };
                const colors = getPriceTypeColor(priceObj.price_type);
                return (
                  <div key={index} className={`${colors.bg} rounded-lg p-3`}>
                    <p className={`text-xs font-medium ${colors.label} mb-1`}>
                      {getPriceTypeLabel(priceObj.price_type)}
                    </p>
                    <p className={`text-lg font-bold ${colors.text}`}>
                      {formatCurrency(priceObj.price)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-neutral-500 italic">No active prices configured</div>
          )}
        </div>
      </div>
    </div>
  );
}
